/* ==================================================
  c_cart-count.js — ヘッダーのカウントバッジ <c-cart-count>（HDN1794-1796）
  - 受注区分 2/3（店舗受取）では配送日時指定UI非表示用ダミー商品（¥0・顧客には見えない行）が
    カートに入り、実商品がある間は購入ボタン押下まで残る（BUG-03 / ADR 0001。
    assets/c_cart-dummy-cleanup.js 参照）。
  - Focal の <cart-count>（assets/theme.js の CartCount）は cart:updated / cart:refresh のたびに
    生の item_count を書く。snippets/js-variables.liquid が pageshow（通常ロード・bfcache 復元の
    両方）ごとに /cart.js を取り直して cart:refresh を投げるため、Liquid が控除した点数は表示直後に
    上書きされ、バッジだけがダミー分多くなる（/checkout から /cart に戻ると行数と食い違う）。
  - テーマ本体（theme.js）は編集禁止（reference/coding-rules.md）なので、バッジを Tabio 独自の
    本要素に差し替え、同じ 2 イベントを購読してダミーを除いた点数を書く。Focal の CartCount は
    本要素を知らないため、書き込みの競合（イベント順・非同期 fetch の後勝ち）は起きない。
  - 【重要】表示だけを直す。カートの中身（ダミー行・受注区分属性）には一切触らない。
    ダミーの寿命は c_cart-dummy-cleanup.js と購入ボタン（c_main-cart.js）の担当のまま。
  - ダミーの SKU は sections/header.liquid が data-dummy-sku で渡す（出所は Shop メタフィールド
    tabio.dummy_product_sku の 1 箇所のみ）。未設定なら控除しない: 空文字比較で SKU 無しの
    通常商品を巻き込むため（Liquid 側と同じ規則）。
===================================================*/
(() => {
  'use strict';

  /* Focal の CartCount と同じ 2 イベント。documentElement で投げられるか bubbles で届く。 */
  const CART_EVENTS = ['cart:updated', 'cart:refresh'];

  function cartJsonUrl() {
    const root = (window.Shopify?.routes?.root || '/').replace(/\/$/, '');
    const base = window.themeVariables?.routes?.cartUrl || `${root}/cart`;
    return `${base}.js`;
  }

  /* 純関数: 顧客に見える点数 = item_count − ダミー行の数量。
     item_count が数値として読めない（想定外の payload）なら null = 表示を変えない。 */
  function visibleItemCount(cart, dummySku) {
    const total = Number(cart && cart.item_count);
    if (!cart || !Number.isFinite(total)) return null;
    if (!dummySku) return total;
    const dummyQuantity = (cart.items || [])
      .filter((item) => item && item.sku === dummySku)
      .reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
    return Math.max(0, total - dummyQuantity);
  }

  /* 最後に受けたイベントだけを反映する更新器。detail 無しのイベントは fetchCart（/cart.js）で補う。
     遅れて返った古い fetch が新しいイベントの結果を上書きしないよう、受付順の番号で捨てる。
     fetch 失敗時は表示を変えない（次のイベント・次のページ表示で自己回復する）。 */
  function createCountUpdater(write, fetchCart, dummySku) {
    let latest = 0;
    return async function update(cart) {
      const ticket = ++latest;
      let resolved = cart;
      if (!resolved) {
        try {
          resolved = await fetchCart();
        } catch (error) {
          return;
        }
      }
      if (ticket !== latest) return;
      const count = visibleItemCount(resolved, dummySku);
      if (count !== null) write(count);
    };
  }

  window.CCartCount = { visibleItemCount, createCountUpdater, CART_EVENTS };

  /* node:vm のテストには DOM が無い。純関数だけ公開して終わる。 */
  if (!window.customElements || !window.HTMLElement || window.customElements.get('c-cart-count')) return;

  async function fetchCart() {
    const res = await fetch(cartJsonUrl(), { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`cart.js ${res.status}`);
    return res.json();
  }

  class CCartCount extends window.HTMLElement {
    connectedCallback() {
      if (this._onCartEvent) return;
      const update = createCountUpdater(
        (count) => { this.textContent = String(count); },
        fetchCart,
        this.dataset.dummySku || ''
      );
      this._onCartEvent = (event) => { void update(event?.detail?.cart); };
      CART_EVENTS.forEach((name) => document.documentElement.addEventListener(name, this._onCartEvent));
    }

    disconnectedCallback() {
      if (!this._onCartEvent) return;
      CART_EVENTS.forEach((name) => document.documentElement.removeEventListener(name, this._onCartEvent));
      this._onCartEvent = null;
    }
  }

  window.customElements.define('c-cart-count', CCartCount);
})();
